import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Session } from './AuthContext';
import { BrandingPaletteOptions, ShapeOptions } from '../system/branding';
import {
  BreakpointsOptions,
  TransitionsOptions,
  TypographyVariants,
} from '@mui/material';
import { ZIndexOptions } from '@mui/material/styles/zIndex';
import { useTenant } from './TenantContext';

export interface Branding {
  name: string;
  version: string;
  customcss?: string;
  customheader?: string;
  customfooter?: string;
  customscripts?: CustomScript[];
  logo?: Logo;
  isdarktheme?: boolean;
  themeoptions?: BrandingThemeOptions;
  selectedTheme?: string;
  usecustomheaderandfooterunauthenticated?: boolean;
  customunauthenticatedheader?: string;
  customunauthenticatedfooter?: string;
}

export interface CustomScript {
  content: string;
  loadAsync: boolean;
}

export interface Logo {
  url: string;
  altText: string;
  width: number;
  height: number;
}

export interface BrandingThemeOptions {
  palette?: Partial<BrandingPaletteOptions>;
  typography?: Partial<TypographyVariants>;
  shape?: Partial<ShapeOptions>;
  breakpoints?: Partial<BreakpointsOptions>;
  spacing?: number | ((factor: number) => number | string);
  zIndex?: Partial<ZIndexOptions>;
  transitions?: Partial<TransitionsOptions>;
}

export interface BrandContextType {
  branding: Branding | null;
  isLoading: boolean;
  error: Error | null;
  loadBranding: (session: Session) => Promise<void>;
  clearBranding: () => void;
  hasCachedBranding: boolean;
  shouldShowUnauthenticatedContent: boolean;
  applyCustomLogo: (
    logoUrl?: string,
    altText?: string,
    width?: number,
    height?: number,
  ) => void;
}

export const BrandContext = createContext<BrandContextType | undefined>(
  undefined,
);

export const BrandProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { api } = useTenant();
  const [branding, setBranding] = useState<Branding | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasCachedBranding, setHasCachedBranding] = useState<boolean>(false);
  const [
    shouldShowUnauthenticatedContent,
    setShouldShowUnauthenticatedContent,
  ] = useState<boolean>(false);
  const isLoadingRef = useRef(false);

  // Check for cached branding on initial load and apply it immediately
  useEffect(() => {
    const cachedBranding = localStorage.getItem('branding');
    const cachedTimestamp = localStorage.getItem('brandingTimestamp');

    if (cachedBranding && cachedTimestamp) {
      try {
        const parsedBranding: Branding = JSON.parse(cachedBranding);
        setHasCachedBranding(true);

        // Apply cached branding immediately - don't wait for authentication
        setBranding(parsedBranding);
        applyCustomCSS(parsedBranding.customcss);
        applyCustomScripts(parsedBranding.customscripts);

        // Apply logo if it exists
        if (parsedBranding.logo?.url && !parsedBranding.customheader) {
          applyCustomLogo(
            parsedBranding.logo.url,
            parsedBranding.logo.altText,
            parsedBranding.logo.width,
            parsedBranding.logo.height,
          );
        }

        // Determine if we should show unauthenticated content
        setShouldShowUnauthenticatedContent(
          calculateShouldShowUnauthenticatedContent(parsedBranding),
        );
      } catch (err) {
        console.error('Error parsing cached branding:', err);
        localStorage.removeItem('branding');
        localStorage.removeItem('brandingTimestamp');
        setHasCachedBranding(false);
      }
    }
  }, []);

  // Helper function to determine if we should show unauthenticated content
  const calculateShouldShowUnauthenticatedContent = (
    branding: Branding | null,
  ): boolean => {
    if (!branding) return false;

    // If the flag is true, we use the regular customheader/customfooter for unauthenticated state
    if (branding.usecustomheaderandfooterunauthenticated) {
      return !!branding.customheader || !!branding.customfooter;
    }

    // Otherwise, check if we have specific unauthenticated content defined
    return (
      !!branding.customunauthenticatedheader ||
      !!branding.customunauthenticatedfooter
    );
  };

  const applyCustomLogo = (
    logoUrl?: string,
    altText?: string,
    width?: number,
    height?: number,
  ) => {
    if (!logoUrl) return;

    // First, add a stylesheet for the logo replacement
    const styleId = 'custom-logo-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    // Create if it doesn't exist
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    // Define dimensions with fallbacks
    const logoWidth = width || 'auto';
    const logoHeight = height || '30px';

    // Create the CSS ruleset without using custom properties
    const css = `
    /* Hide the original logo */
    #bright-megamenu img.bn-brand {
      opacity: 0 !important;
      visibility: hidden;
    }
    
    /* Container for positioning */
    #bright-megamenu .bn-col-fixed-242 {
    }
    
    /* Add the custom logo as a pseudo-element */
    #bright-megamenu .bn-col-fixed-242::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${logoWidth}px;
      height: ${logoHeight}px;
      background-image: url("${logoUrl}");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      z-index: 1000;
    }
  `;

    styleElement.textContent = css;

    // Also update logo attributes directly if the element exists
    // This is a backup in case the CSS doesn't apply immediately
    setTimeout(() => {
      const logoImg = document.querySelector(
        '#bright-megamenu img.bn-brand',
      ) as HTMLImageElement;
      if (logoImg) {
        // Store original values for restoration if needed
        if (!logoImg.hasAttribute('data-original-src')) {
          logoImg.setAttribute('data-original-src', logoImg.src);
          logoImg.setAttribute('data-original-alt', logoImg.alt || '');
        }

        // Update attributes (in case CSS fails)
        logoImg.src = logoUrl;
        logoImg.alt = altText || 'Logo';
        if (width) logoImg.width = width;
        if (height) logoImg.height = height;
      }
    }, 100);
  };

  // Check for cached branding on initial load
  useEffect(() => {
    const cachedBranding = localStorage.getItem('branding');
    const cachedTimestamp = localStorage.getItem('brandingTimestamp');

    if (cachedBranding && cachedTimestamp) {
      setHasCachedBranding(true);

      try {
        const parsedBranding: Branding = JSON.parse(cachedBranding);

        // Calculate whether to show unauthenticated content based on the cached branding
        setShouldShowUnauthenticatedContent(
          calculateShouldShowUnauthenticatedContent(parsedBranding),
        );

        // Don't apply branding yet - wait for authentication to complete
        // This prevents flashing of potentially incorrect tenant branding
      } catch (err) {
        console.error('Error parsing cached branding:', err);
        localStorage.removeItem('branding');
        localStorage.removeItem('brandingTimestamp');
        setHasCachedBranding(false);
      }
    }
  }, []);

  const loadBranding = useCallback(async (session: Session) => {
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      const cachedBranding = localStorage.getItem('branding');
      const cachedTimestamp = localStorage.getItem('brandingTimestamp');

      if (cachedBranding && cachedTimestamp) {
        const currentTime = new Date().getTime();
        const cacheAge = currentTime - parseInt(cachedTimestamp, 10);

        if (cacheAge < 7 * 24 * 60 * 60 * 1000) {
          const parsedBranding: Branding = JSON.parse(cachedBranding);

          // Verify the branding belongs to the current tenant
          if (
            session?.user?.tenant &&
            parsedBranding.name !== session.user.tenant
          ) {
            console.log(
              'Cached branding is for a different tenant, fetching new branding',
            );
            await fetchBranding(session);
          } else {
            applyCustomCSS(parsedBranding.customcss);
            applyCustomScripts(parsedBranding.customscripts);
            setBranding(parsedBranding);
            setShouldShowUnauthenticatedContent(
              calculateShouldShowUnauthenticatedContent(parsedBranding),
            );
            // Apply logo if it exists
            if (parsedBranding.logo?.url && !parsedBranding.customheader) {
              applyCustomLogo(
                parsedBranding.logo.url,
                parsedBranding.logo.altText,
                parsedBranding.logo.width,
                parsedBranding.logo.height,
              );
            }
            setIsLoading(false);

            // Check version
            const latestVersion = await fetch(
              `${api}/get-branding-version?system=${session.user.tenant}`,
              {
                credentials: 'include',
              },
            ).then((res) => res.text());

            if (latestVersion && parsedBranding.version !== latestVersion) {
              await fetchBranding(session);
            }
          }

          return;
        }
      }

      await fetchBranding(session);
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error ? err : new Error('Failed to load branding'),
      );
      setIsLoading(false);
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  const fetchBranding = async (session: Session) => {
    try {
      const brandingData = await fetch(
        `${api}/get-branding?system=${session.user.tenant}`,
        {
          credentials: 'include',
        },
      ).then((r) => r.json());

      setBranding(brandingData);
      localStorage.setItem('branding', JSON.stringify(brandingData));
      localStorage.setItem(
        'brandingTimestamp',
        new Date().getTime().toString(),
      );
      setShouldShowUnauthenticatedContent(
        calculateShouldShowUnauthenticatedContent(brandingData),
      );

      applyCustomCSS(brandingData.customcss);
      applyCustomScripts(brandingData.customscripts);
    } catch (error) {
      console.error('Error fetching branding:', error);
      setError(
        error instanceof Error ? error : new Error('Failed to fetch branding'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clearBranding = () => {
    localStorage.removeItem('branding');
    localStorage.removeItem('brandingTimestamp');
    setBranding(null);
    setHasCachedBranding(false);
    setShouldShowUnauthenticatedContent(false);

    // Remove any custom CSS that was applied
    const customStyleElements = document.querySelectorAll(
      'style[data-branding="custom"]',
    );
    customStyleElements.forEach((element) => element.remove());

    // Remove custom logo styles
    const logoStyles = document.getElementById('custom-logo-styles');
    if (logoStyles) {
      logoStyles.remove();
    }

    // Restore original logo if it exists
    const logoImg = document.querySelector(
      '#bright-megamenu img.bn-brand',
    ) as HTMLImageElement;
    if (logoImg && logoImg.hasAttribute('data-original-src')) {
      logoImg.src = logoImg.getAttribute('data-original-src') || '';
      logoImg.alt = logoImg.getAttribute('data-original-alt') || '';
      logoImg.removeAttribute('data-original-src');
      logoImg.removeAttribute('data-original-alt');
    }
  };

  const applyCustomCSS = (customCSS?: string) => {
    if (customCSS) {
      // Remove any existing custom branding styles first
      const existingStyle = document.querySelector(
        'style[data-branding="custom"]',
      );
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.textContent = customCSS;
      style.setAttribute('data-branding', 'custom'); // Add attribute to identify this style

      document.head.appendChild(style);
    }
  };

  const applyCustomScripts = (customScripts?: CustomScript[]) => {
    if (customScripts) {
      customScripts.forEach((script) => {
        const scriptElement = document.createElement('script');
        scriptElement.textContent = script.content;
        scriptElement.async = script.loadAsync;
        scriptElement.setAttribute('data-branding', 'custom'); // Add attribute to identify this script
        document.body.appendChild(scriptElement);
      });
    }
  };

  const BrandContextValue: BrandContextType = useMemo(() => {
    return {
      branding,
      isLoading,
      error,
      loadBranding,
      clearBranding,
      hasCachedBranding,
      shouldShowUnauthenticatedContent,
      applyCustomLogo,
    };
  }, [
    branding,
    error,
    hasCachedBranding,
    isLoading,
    loadBranding,
    shouldShowUnauthenticatedContent,
  ]);

  return (
    <BrandContext.Provider value={BrandContextValue}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};
